import { Injectable } from '@angular/core';
import {
  ChildLinkOutcome,
  DuplicateCheck,
  DuplicateCheckLink,
  DuplicateCheckLock,
  DuplicateCheckTrigger,
  ResolutionStatus,
} from './duplicate-checks.model';

import { Page } from '../../../../common/paging-dto';
import {
  DuplicateCheckDTO,
  DuplicateCheckLinkDTO,
  DuplicateCheckLockDTO,
  DuplicateCheckPlayerDataDTO,
} from './duplicate-checks.dto';

@Injectable({
  providedIn: 'root',
})
export class DuplicateChecksMapper {
  toBackendOutcome(outcome: ChildLinkOutcome): string {
    switch (outcome) {
      case ChildLinkOutcome.MITIGATED:
        return 'MITIGATED';
      case ChildLinkOutcome.LINKED_SIMILAR_DETAILS:
        return 'LINKED_SIMILAR_DETAILS';
      case ChildLinkOutcome.LINKED_HOUSEHOLD:
        return 'LINKED_HOUSEHOLD';
      case ChildLinkOutcome.LINKED_DUPLICATE:
        return 'LINKED_DUPLICATE';
      case ChildLinkOutcome.LINKED_PAR:
        return 'LINKED_PAR';
      case ChildLinkOutcome.LINKED_DUPLICATE_WITH_STATUS_CHANGE:
        return 'LINKED_DUPLICATE_WITH_STATUS_CHANGE';
    }
  }

  toModelPage(dto: Page<DuplicateCheckDTO>): Page<DuplicateCheck> {
    return {
      pageNumber: dto.pageNumber,
      pageSize: dto.pageSize,
      total: dto.total,
      content: (dto.content || []).map((c) => this.toModelCheck(c)),
    } as Page<DuplicateCheck>;
  }

  toModelCheck(dto: DuplicateCheckDTO): DuplicateCheck {
    const player = dto.playerData;
    const base = this.fromPlayerData(player);
    const children: DuplicateCheckLink[] = (dto.duplicates || []).map((d) => this.toModelLink(d));

    return {
      id: dto.id,
      pid: dto.playerId,
      lockedBy: this.toModelLock(dto.lockedBy),
      ...base,
      trigger: DuplicateCheckTrigger[dto.trigger],
      created: this.ensureDate(dto.checkDate),
      finished: this.ensureDate(dto.archivedDate),
      resolutionStatus: this.resolveResolutionStatus(dto.outcome, dto.archivedAutomatically),
      checkedFields: this.resolveCheckedFields(player as any),
      children,
    } as DuplicateCheck;
  }

  private toModelLink(dto: DuplicateCheckLinkDTO): DuplicateCheckLink {
    const p = dto.player;
    const flattened = this.fromPlayerData(p);
    return {
      checkId: dto.checkId,
      pid: dto.playerId,
      outcome: ChildLinkOutcome[dto.outcome],
      ...flattened,
      reason: dto.outcomeReason,
      actionedBy: dto.actionedBy,
      actionedDate: this.ensureDate(dto.outcomeDate),
      linkChecked: dto.linkChecked,
      existingLink: dto.existingLink && { relation: dto.existingLink.relation, type: dto.existingLink.type },
      checkedFields: this.resolveCheckedFields(p as any),
    } as DuplicateCheckLink;
  }

  private toModelLock(dto?: DuplicateCheckLockDTO): DuplicateCheckLock | undefined {
    if (!dto) {
      return undefined;
    }
    return {
      email: dto.agentEmail,
      fullName: dto.agentName,
      currentOperator: dto.currentOperator,
    } as DuplicateCheckLock;
  }

  private fromPlayerData(dto: DuplicateCheckPlayerDataDTO) {
    return {
      firstName: dto?.firstName,
      lastName: dto?.lastName,
      email: dto?.email,
      dob: dto?.dob,
      mobile: dto?.mobileNumber,
      houseNo: dto?.houseNo,
      postcode: dto?.postcode,
      registered: this.ensureDate(dto?.registrationDate),
      status: dto?.status,
      seonScore: dto?.seonScore,
    } as Pick<
      DuplicateCheck,
      | 'firstName'
      | 'lastName'
      | 'email'
      | 'dob'
      | 'mobile'
      | 'houseNo'
      | 'postcode'
      | 'registered'
      | 'status'
      | 'seonScore'
    >;
  }

  private ensureDate(val: any): Date | undefined {
    if (val === null || val === undefined) {
      return undefined;
    }
    return val instanceof Date ? val : new Date(val);
  }

  private resolveCheckedFields(playerData: any) {
    if (!playerData) {
      return undefined;
    }

    if (playerData.checkedFields) {
      return playerData.checkedFields;
    }
    const list: string[] = playerData['duplicated-fields'] || playerData['duplicatedFields'];
    if (!Array.isArray(list) || list.length === 0) {
      return undefined;
    }
    const norm = new Set(list.map((s) => String(s).toUpperCase()));
    return {
      firstName: norm.has('FIRST_NAME'),
      lastName: norm.has('LAST_NAME'),
      email: norm.has('EMAIL'),
      dob: norm.has('DOB'),
      mobile: norm.has('MOBILE') || norm.has('MOBILE_NUMBER') || norm.has('MOBILE-NUMBER'),
      houseNo: norm.has('HOUSE_NO') || norm.has('HOUSE-NO') || norm.has('HOUSE'),
      postcode: norm.has('POST_CODE') || norm.has('POST-CODE') || norm.has('POSTCODE'),
    };
  }

  private resolveResolutionStatus(outcome?: string, archivedAutomatically?: boolean): ResolutionStatus | undefined {
    if (!outcome) {
      return undefined;
    }

    switch (outcome) {
      case 'AUTO_PAR':
        return ResolutionStatus.AUTO_PAR;
      case 'MITIGATED':
        return ResolutionStatus.MANUAL_MITIGATION;
      case 'AUTO_MITIGATED':
        return ResolutionStatus.AUTO_MITIGATION;
      case 'DUPLICATE':
        return archivedAutomatically ? ResolutionStatus.AUTO_DUPLICATE : ResolutionStatus.MANUAL_DUPLICATE;
      case 'AUTO_DUPLICATE':
        return ResolutionStatus.AUTO_DUPLICATE;
      case 'SIMILARITY':
        return ResolutionStatus.MANUAL_SIMILARITY;
      default:
        return undefined;
    }
  }
}
